import {
  CURRENT_TASKS_TYPE,
  ICONS,
  MORE_TASKS_LOADING,
  NETWORK_EXTRA_METADATA_UPDATE,
  PROGRESS_COUNT,
  PROGRESS_FAILURE,
  PROGRESS_LOADING,
  PROGRESS_SUCCESS,
  REPORT_ICONS,
  RESOURCE_REPORT_SUCCESS,
  RESOURCE_SUCCESS,
  TASK_STATUS_UPDATE,
  TASKS_LIST_CLEAR,
  TASKS_LIST_FAILURE,
  TASKS_LIST_LOADING,
  TASKS_LIST_SEARCH,
  TASKS_LIST_SET_SEARCH,
  TASKS_LIST_SUCCESS,
  TASKS_STATUS_FAILURE,
  TASKS_STATUS_LOADING,
  TASKS_STATUS_SUCCESS,
} from './constants';
import { createReducer } from './utils';
import { clearAbort, createAbort, ABORT_KEYS } from './utils/abortManager';
import { countActionableProgress } from './utils/progressCounts';

export const DEFAULT_PAGE_SIZE = 10;

// Monotonic request ids for getTaskList. Two independent counters because the
// two responses they gate are independent:
//   - `taskListReqId`: bumps on EVERY call; gates the paginated concat (any
//     newer call — context change OR next page — invalidates the previous
//     concat because it could overwrite a newer list).
//   - `taskListFullReqId`: bumps only on `page === 0`; gates the full-list
//     fetch that feeds `tasksFilterSearchList`. A later `getTaskList(1)` for
//     pagination does NOT invalidate the full fetch — they're separate flows.
let taskListReqId = 0;
let taskListFullReqId = 0;

const defaultState = {
  errorMessage: null,
  //tasks
  tasksList: [],
  tasksFilterList: [],
  tasksFilterSearchList: [],
  taskLoading: false,
  moreTasksLoading: false,
  //progress
  progressLoading: true,
  progressTasksType: {},
  progressTasksTypeBaseline: {},
  resourcesTasksType: [],
  resourcesReportOptions: [],
  totalTasks: 0,
  pendingTasks: 0,
  totalTasksBaseline: 0,
  pendingTasksBaseline: 0,
  failedProgress: false,
  progressLoadedStore: null,
  //status
  tasksStatusLoading: false,
  tasksStatus: [],
  currentTaskType: '',
  //icons
  icons: {},
  reportIcons: {},
};

// reducer

export const reducer = createReducer(defaultState, {
  [TASKS_LIST_LOADING]: handleTasksListLoading,
  [TASKS_LIST_SUCCESS]: handleTasksListSuccess,
  [TASKS_LIST_FAILURE]: handleTasksListFailure,
  [TASKS_LIST_CLEAR]: handleTasksListClear,
  [TASKS_LIST_SEARCH]: handleTasksListSearch,
  [TASKS_LIST_SET_SEARCH]: handleTasksListSetSearch,
  [MORE_TASKS_LOADING]: handleMoreTasksLoading,
  [PROGRESS_LOADING]: handleProgressLoading,
  [PROGRESS_SUCCESS]: handleProgressSuccess,
  [RESOURCE_SUCCESS]: handleResourceSuccess,
  [RESOURCE_REPORT_SUCCESS]: handleResourceReportSuccess,
  [PROGRESS_FAILURE]: handleProgressFailure,
  [PROGRESS_COUNT]: handleProgressCount,
  [TASKS_STATUS_LOADING]: handleTasksStatusLoading,
  [TASKS_STATUS_SUCCESS]: handleTasksStatusSuccess,
  [TASKS_STATUS_FAILURE]: handleTasksStatusFailure,
  [CURRENT_TASKS_TYPE]: handleCurrentTasksType,
  [ICONS]: handleIcons,
  [REPORT_ICONS]: handleReportIcons,
  [TASK_STATUS_UPDATE]: handleTaskStatusUpdate,
});

// Tasks List Handlers
function handleTasksListLoading(state, action) {
  return {
    ...state,
    taskLoading: true,
  };
}

function handleMoreTasksLoading(state, action) {
  return {
    ...state,
    moreTasksLoading: true,
  };
}

function handleTasksListSuccess(state, { payload: { newTaskList } }) {
  return {
    ...state,
    taskLoading: false,
    moreTasksLoading: false,
    tasksList: newTaskList,
    tasksFilterList: newTaskList,
  };
}

function handleTasksListFailure(state, { payload: { error } }) {
  return {
    ...state,
    taskLoading: false,
    moreTasksLoading: false,
    tasksList: [],
    tasksFilterList: [],
    errorMessage: error,
  };
}

function handleTasksListClear(state, action) {
  return {
    ...state,
    taskLoading: false,
    moreTasksLoading: false,
    tasksList: [],
    tasksFilterList: [],
    tasksFilterSearchList: [],
  };
}

function handleTasksListSearch(state, { payload: { newTaskList } }) {
  return {
    ...state,
    taskLoading: false,
    moreTasksLoading: false,
    tasksFilterList: newTaskList,
  };
}

function handleTasksListSetSearch(
  state,
  { payload: { fullRetrivedTaskList } },
) {
  // `makeRequest` falls back to the raw response text when a 2xx body does not
  // parse as JSON, so this payload is not guaranteed to be a list. Both the
  // search filter and `handleTaskUpdate` call array methods on it, and a string
  // reaches them as a crash rather than an empty result.
  if (!Array.isArray(fullRetrivedTaskList)) {
    console.log(
      'getTaskList: expected a task array for the search list, got',
      typeof fullRetrivedTaskList,
    );
    return { ...state, tasksFilterSearchList: [] };
  }

  return {
    ...state,
    tasksFilterSearchList: fullRetrivedTaskList,
  };
}

// Progress List Handlers
function handleProgressLoading(state, action) {
  return {
    ...state,
    progressLoading: true,
    failedProgress: false,
  };
}

function handleProgressSuccess(state, { payload: { progressTasksType, progressTasksTypeBaseline, store } }) {
  return {
    ...state,
    progressLoading: false,
    progressTasksType,
    // A payload without a baseline (setCategory in categories.js) doubles as
    // its own baseline, keeping both fields on the same lifecycle.
    progressTasksTypeBaseline: progressTasksTypeBaseline ?? progressTasksType,
    failedProgress: false,
    progressLoadedStore: store,
  };
}

function handleResourceSuccess(state, { payload: { resourcesTasksType } }) {
  return {
    ...state,
    resourcesTasksType,
  };
}

function handleResourceReportSuccess(
  state,
  { payload: { resourcesReportOptions } },
) {
  return {
    ...state,
    resourcesReportOptions,
  };
}

function handleProgressFailure(state, { payload: { error, store } }) {
  return {
    ...state,
    progressLoading: false,
    failedProgress: true,
    errorMessage: error,
    progressLoadedStore: store,
  };
}

function handleProgressCount(
  state,
  { payload: { total, pending, totalBaseline, pendingBaseline } },
) {
  return {
    ...state,
    totalTasks: total,
    pendingTasks: pending,
    // Same convention as progressTasksTypeBaseline: a payload without baseline
    // counts was not narrowed by the task filters, so it doubles as its own.
    totalTasksBaseline: totalBaseline ?? total,
    pendingTasksBaseline: pendingBaseline ?? pending,
  };
}

// Tasks Status Handlers
function handleTasksStatusLoading(state, action) {
  return {
    ...state,
    tasksStatusLoading: true,
  };
}

function handleTasksStatusSuccess(state, { payload: { possible_status } }) {
  return {
    ...state,
    tasksStatusLoading: false,
    tasksStatus: possible_status,
  };
}

function handleTasksStatusFailure(state, { payload: { error } }) {
  return {
    ...state,
    tasksStatusLoading: false,
    tasksStatus: [],
    errorMessage: error,
  };
}

// current task type
function handleCurrentTasksType(state, { payload: { task_type } }) {
  return {
    ...state,
    currentTaskType: task_type,
  };
}

// Icons
function handleIcons(state, { payload: { icons } }) {
  return {
    ...state,
    icons: icons,
  };
}

function handleReportIcons(state, { payload: { reportIcons } }) {
  return {
    ...state,
    reportIcons: reportIcons,
  };
}

function handleTaskStatusUpdate(state, { payload: { taskId, status } }) {
  const update = task => task.task_id === taskId ? { ...task, status } : task;
  return {
    ...state,
    tasksList: state.tasksList.map(update),
    tasksFilterList: state.tasksFilterList.map(update),
    tasksFilterSearchList: state.tasksFilterSearchList.map(update),
  };
}

// actions

export function clearTaskList() {
  return async (dispatch, getState, { services: { } }) => {
    dispatch({ type: TASKS_LIST_CLEAR });
  };
}

export function loadTaskList() {
  return async (dispatch, getState, { services: { } }) => {
    dispatch({ type: TASKS_LIST_LOADING });
  };
}

export function loadMoreTasks() {
  return async (dispatch, getState, { services: { } }) => {
    dispatch({ type: MORE_TASKS_LOADING });
  };
}

export function setCurrentTaskType(task_type) {
  return async (dispatch, getState, { services: { } }) => {
    dispatch({ type: CURRENT_TASKS_TYPE, payload: { task_type } });
  };
}

export function setSupplierId(supplierId) {
  return async (dispatch, getState, { services: { } }) => {
    dispatch({ type: CURRENT_TASKS_TYPE, payload: { supplierId } });
  };
}

export function updateTaskStatusLocally(taskId, status) {
  return (dispatch) => {
    dispatch({ type: TASK_STATUS_UPDATE, payload: { taskId, status } });
  };
}

export function getTaskList(page) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    const {
      stores: { currentStore },
      tasks: { tasksList },
      filters: { filters },
      tasks: { currentTaskType },
      categories: { currentKey },
    } = getState();

    try {
      let parameters = Object.assign({}, filters);

      // stockout filter
      if (currentTaskType !== 'stockout' || parameters['spread_to_fill'] == null) {
        delete parameters['spread_to_fill'];
      }

      parameters['task_type'] = currentTaskType;
      parameters['store'] = currentStore;
      parameters['page'] = page;
      parameters['page_size'] = DEFAULT_PAGE_SIZE;

      if (currentKey !== null) {
        parameters['main_category'] = currentKey;
      }

      // Remove null/undefined/empty-string params
      Object.keys(parameters).forEach((key) => {
        if (parameters[key] === null || parameters[key] === undefined || parameters[key] === '') {
          delete parameters[key];
        }
      });

      const myReqId = ++taskListReqId;
      // Only page 0 runs the full-list fetch below, so only bump that counter here.
      const myFullReqId = page === 0 ? ++taskListFullReqId : taskListFullReqId;
      const listSignal = createAbort(ABORT_KEYS.TASK_LIST);
      const retrivedTaskList = await dataSource.requestTask({
        parameters: parameters,
        signal: listSignal,
      });
      clearAbort(ABORT_KEYS.TASK_LIST);

      // Drop stale responses: if a newer getTaskList started (context change
      // or next page), myReqId will be behind taskListReqId. Concatenating
      // onto tasksList here would overwrite the newer list.
      if (myReqId === taskListReqId) {
        // Same makeRequest fallback the search list has to guard against: a 2xx
        // body that does not parse as JSON comes back as raw text, and concat
        // would push that whole string into the list as one element for the UI to
        // render as a task. Fail the list instead of showing a fabricated row.
        if (!Array.isArray(retrivedTaskList)) {
          console.log(
            'getTaskList: expected a task array for the list, got',
            typeof retrivedTaskList,
          );
          dispatch({
            type: TASKS_LIST_FAILURE,
            payload: { error: new Error('Malformed task list response') },
          });
          return;
        }

        // page 0 always starts fresh — don't concat stale state that may not
        // have been cleared yet when clearTaskList and getTaskList race as async thunks.
        const base = page === 0 ? [] : tasksList;
        let newTaskList = base.concat(retrivedTaskList);

        if (retrivedTaskList.length !== 0) {
          if (
            Object.keys(newTaskList[0]).length === 0 &&
            newTaskList[0].constructor === Object
          ) {
            newTaskList = [];
          }
        }

        dispatch({
          type: TASKS_LIST_SUCCESS,
          payload: { newTaskList },
        });
      }

      // Deliberately outside the staleness check above: scrolling fires
      // getTaskList(1), which supersedes the concat but leaves the search list
      // alone. Returning early on that would skip this fetch for the rest of
      // the session and leave search matching nothing. Only a newer page-0 call
      // invalidates it, and testing that before createAbort keeps this call from
      // cancelling the newer one's in-flight request.
      if (page === 0 && myFullReqId === taskListFullReqId) {
        let filter_parameters = Object.assign({}, parameters);
        delete filter_parameters['page_size'];
        delete filter_parameters['page'];
        const listSearchSignal = createAbort(ABORT_KEYS.TASK_LIST_SEARCH);
        const fullRetrivedTaskList = await dataSource.requestTask({
          parameters: filter_parameters,
          signal: listSearchSignal,
        });
        clearAbort(ABORT_KEYS.TASK_LIST_SEARCH);
        if (myFullReqId !== taskListFullReqId) return;
        dispatch({
          type: TASKS_LIST_SET_SEARCH,
          payload: { fullRetrivedTaskList },
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (myReqId !== taskListReqId) return;
      console.log('getTaskList error', error);
      dispatch({
        type: TASKS_LIST_FAILURE,
        payload: { error },
      });
    }
  };
}

export function setTasksFilter(newTaskList) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    try {
      const newList = newTaskList !== undefined ? newTaskList : [];
      dispatch({
        type: TASKS_LIST_SEARCH,
        payload: { newTaskList: newList },
      });
    } catch (error) {
      console.log(error);
    }
  };
}

export function getTasksProgress() {
  return async (dispatch, getState, { services: { dataSource } }) => {
    dispatch({ type: PROGRESS_LOADING });

    const {
      stores: { currentStore },
      categories: { currentKey },
      filters: { filters, supplierSelected },
      tasks: { currentTaskType },
    } = getState();

    try {
      const filter = {
        store: currentStore,
      };

      if (currentKey !== null) {
        filter['main_category'] = currentKey;
      }

      // supplierSelected is null when the filter modal applies without a
      // supplier picked, so it is read defensively.
      if (supplierSelected?.id && supplierSelected.id !== 'all') {
        filter['supplier_id'] = supplierSelected.id;
      }

      // The header counter reads these counts, so every filter that narrows the
      // list has to narrow them too. `completed` is deliberately left out:
      // consumers derive the completed count as total - pending, which only
      // holds while the counts describe both halves of the universe.
      if (filters?.overhead) {
        filter['overhead'] = true;
      }

      if (filters?.priority) {
        filter['priority'] = true;
      }

      if (filters?.aisle) {
        filter['aisle'] = filters['aisle'];
      }

      // spread_to_fill only exists for stockout, the same guard getTaskList applies.
      if (currentTaskType === 'stockout' && filters?.spread_to_fill != null) {
        filter['spread_to_fill'] = filters['spread_to_fill'];
      }

      // Baseline counts ignore the modal filters (everything beyond store and
      // main_category) so the header can render filtered/total. The extra
      // request is only worth it while a modal filter actually narrows the
      // counts; otherwise the filtered response already IS the baseline.
      const baselineFilter = { store: currentStore };
      if (currentKey !== null) {
        baselineFilter['main_category'] = currentKey;
      }
      const hasNarrowingFilters = Object.keys(filter).some(
        key => !(key in baselineFilter),
      );

      const startTime = Date.now();
      const progressSignal = createAbort(ABORT_KEYS.TASKS_PROGRESS);
      const [progressTasksType, resourcesTasksType, resourcesReportOptions, baselineResponse] =
        await Promise.all([
          dataSource.requestProgressTasksType(filter, progressSignal),
          dataSource.requestResourcesTasksType({ store: currentStore }, progressSignal),
          dataSource.requestResourcesReportOptions({ store: currentStore }, progressSignal),
          // The baseline is cosmetic (it only feeds the "/total" half of the
          // header counter), so its failure must not sink the primary load.
          // An abort still cancels everything through the primary requests.
          hasNarrowingFilters
            ? dataSource
              .requestProgressTasksType(baselineFilter, progressSignal)
              .catch(() => null)
            : null,
        ]);
      clearAbort(ABORT_KEYS.TASKS_PROGRESS);
      const progressTasksTypeBaseline = baselineResponse ?? progressTasksType;
      const totalTime = Date.now() - startTime;
      const progressTasksTypeTime = totalTime;
      const resourcesTasksTypeTime = totalTime;
      const resourcesReportOptionsTime = totalTime;

      const { total, pending } = countActionableProgress(progressTasksType);
      // The Home reads the baseline counts, so they have to be derived from the
      // baseline response and not from the filtered one.
      const { total: totalBaseline, pending: pendingBaseline } =
        countActionableProgress(progressTasksTypeBaseline);

      let icons = {};
      resourcesTasksType.map(element => {
        icons[element.task_type] = element.mobile_icon;
      });

      let reportIcons = {};
      resourcesReportOptions.map(element => {
        reportIcons[element.task_type] = element.mobile_icon;
      });

      // Resources must arrive before PROGRESS_SUCCESS so that consumers guarding on
      // progressLoadedStore === currentStore always see up-to-date resourcesTasksType.
      dispatch({
        type: RESOURCE_SUCCESS,
        payload: { resourcesTasksType },
      });
      dispatch({
        type: RESOURCE_REPORT_SUCCESS,
        payload: { resourcesReportOptions },
      });
      dispatch({ type: ICONS, payload: { icons } });
      dispatch({ type: REPORT_ICONS, payload: { reportIcons } });
      dispatch({
        type: PROGRESS_SUCCESS,
        payload: { progressTasksType, progressTasksTypeBaseline, store: currentStore },
      });
      dispatch({
        type: PROGRESS_COUNT,
        payload: { total, pending, totalBaseline, pendingBaseline },
      });
      dispatch({
        type: NETWORK_EXTRA_METADATA_UPDATE,
        payload: {
          extraMetadata: {
            progressTasksTypeTime,
            resourcesTasksTypeTime,
            resourcesReportOptionsTime,
            '/progress/task_type': progressTasksTypeTime + resourcesTasksTypeTime + resourcesReportOptionsTime,
          }
        },
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      dispatch({
        type: PROGRESS_FAILURE,
        payload: { error, store: currentStore },
      });
    }
  };
}

export function getTasksStatus(task_type) {
  return async (dispatch, getState, { services: { dataSource } }) => {
    const {
      stores: { currentStore },
    } = getState();

    dispatch({ type: TASKS_STATUS_LOADING });

    const signal = createAbort(ABORT_KEYS.TASKS_STATUS);

    try {
      const startTime = Date.now();
      const response = await dataSource.requestTasksStatus(
        { store: currentStore, task_type: task_type },
        signal,
      );
      clearAbort(ABORT_KEYS.TASKS_STATUS);
      const possible_status = response[task_type]['possible_status'];
      const possible_statusTime = Date.now() - startTime;

      dispatch({
        type: TASKS_STATUS_SUCCESS,
        payload: { possible_status },
      });
      dispatch({
        type: NETWORK_EXTRA_METADATA_UPDATE,
        payload: {
          extraMetadata: {
            '/tasks/status': possible_statusTime,
          },
        },
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      dispatch({ type: TASKS_STATUS_FAILURE, payload: { error } });
    }
  };
}
